import React, { useState } from 'react';
import OrderSteps from './OrderSteps';
import OrderHistorySheet from './OrderHistorySheet';
import { useDriverOrderHistory } from '../../hooks/useDriverOrderHistory';
import { haversineKm } from '../../utils/priceCalculator';
import { orderTitle } from '../../utils/orderTitle';
import { placeName, toDate, timeLabel, dayLabel, kmLabel } from '../../utils/orderCardFormat';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckLoader } from '../common/truck';
import CarrierOffersSection from './CarrierOffersSection';

const ACTION = {
  matched:           'Registrer henting',
  in_progress:       'Registrer levering',
  delivered_pending: 'Venter på bekreftelse',
};

const kr = n => Number(n).toLocaleString('nb-NO');

const titleOf = orderTitle;

function routeLine(order) {
  const a = order.pickupAddress, b = order.deliveryAddress;
  const km = a?.lat != null && b?.lat != null ? haversineKm(a.lat, a.lng, b.lat, b.lng) : null;
  return `${placeName(a)} → ${placeName(b)}${km != null ? ` · ${kmLabel(km)}` : ''}`;
}

function metaLine(order) {
  const d = toDate(order.scheduledAt);
  return [`#${order.id.slice(-6)}`, d && `${dayLabel(d)} ${timeLabel(d)}`].filter(Boolean).join(' · ');
}

function ActiveCard({ order, onOpen }) {
  const waiting = order.status === 'delivered_pending';
  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={{ minWidth: 0 }}>
          <div style={styles.meta}>{metaLine(order)}</div>
          <div style={styles.title}>{titleOf(order)}</div>
          <div style={styles.sub}>{routeLine(order)}</div>
        </div>
        {order.fixedPrice != null && (
          <div style={styles.priceCol}>
            <div style={styles.priceLabel}>Pris</div>
            <div style={styles.price}>{kr(order.fixedPrice)}</div>
          </div>
        )}
      </div>
      <div style={{ margin: '14px 0' }}><OrderSteps status={order.status} /></div>
      <button
        className={waiting ? 'tippi-btn-secondary' : 'tippi-btn'}
        style={waiting ? styles.btnOutline : styles.btnDark}
        onClick={() => onOpen(order)}
      >
        {ACTION[order.status] ?? 'Åpne oppdrag'}
      </button>
    </div>
  );
}

function PendingCard({ order, bid, onOpen }) {
  return (
    <button style={styles.pending} onClick={() => onOpen(order)}>
      <span style={styles.clockTile} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.textDark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
        </svg>
      </span>
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <span style={styles.pendingTitle}>{titleOf(order)}</span>
        <span style={styles.pendingSub}>Venter på svar fra bestiller</span>
      </span>
      <span style={styles.price}>{kr(bid.amount)}</span>
    </button>
  );
}

function CompletedList() {
  const { history, loading, error } = useDriverOrderHistory();
  const [selected, setSelected] = useState(null);
  if (loading) return <TruckLoader showLabel label="Laster…" size={64} />;
  if (error) return <p style={styles.empty}>Kunne ikke laste fullførte oppdrag.</p>;
  if (history.length === 0) return <p style={styles.empty}>Ingen fullførte oppdrag ennå.</p>;
  return (
    <>
      {history.map(o => (
        <button key={o.id} type="button" style={styles.pending} onClick={() => setSelected(o)}>
          <span style={styles.clockTile} aria-hidden="true">✓</span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <span style={styles.pendingTitle}>{titleOf(o)}</span>
            <span style={styles.pendingSub}>{routeLine(o)}</span>
          </span>
          {o.fixedPrice != null && <span style={styles.price}>{kr(o.fixedPrice)}</span>}
        </button>
      ))}
      {selected && <OrderHistorySheet order={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

// «Mine oppdrag» i bunnarket: aktive oppdrag med steg, bud som venter på svar, og fullførte.
export default function MyOrdersSheet({ activeOrders, pendingOrders, driverBids, onOpenActive, onOpenPending }) {
  const [view, setView] = useState('active');
  const activeCount = activeOrders.length;
  return (
    <div style={styles.root} role="region" aria-label="Mine oppdrag">
      <div style={styles.pills} role="tablist">
        {[['active', 'Aktive', activeCount], ['done', 'Fullførte', 0]].map(([v, label, count]) => {
          const on = view === v;
          return (
            <button key={v} role="tab" aria-selected={on} onClick={() => setView(v)}
                    style={{ ...styles.pill, ...(on ? styles.pillOn : {}) }}>
              {label}{count > 0 && <span style={{ marginLeft: 6 }}>{count}</span>}
            </button>
          );
        })}
      </div>
      <div style={styles.list}>
        {view === 'done' ? <CompletedList /> : (
          <>
            <CarrierOffersSection />
            {activeCount === 0 && pendingOrders.length === 0 && (
              <p style={styles.empty}>Du har ingen aktive oppdrag.<br />Oppdrag du tar eller får tildelt vises her.</p>
            )}
            {activeOrders.map(o => <ActiveCard key={o.id} order={o} onOpen={onOpenActive} />)}
            {pendingOrders.map(o => (
              <PendingCard key={o.id} order={o} bid={driverBids.get(o.id)} onOpen={onOpenPending} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  root:   { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: COLORS.white },
  pills:  { display: 'flex', gap: 8, padding: '14px 20px 10px' },
  pill:   { padding: '9px 18px', borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  pillOn: { background: COLORS.primary, borderColor: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700 },
  list:   { flex: 1, overflowY: 'auto', padding: '0 20px 24px', display: 'flex', flexDirection: 'column' },
  card:   { background: COLORS.white, borderBottom: `1px solid ${COLORS.border}`, padding: '16px 0' },
  cardTop:{ display: 'flex', justifyContent: 'space-between', gap: 12 },
  meta:   { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  title:  { fontFamily: FONTS.display, fontSize: 30, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark, margin: '2px 0' },
  sub:    { fontSize: 14, color: COLORS.textMid },
  priceCol:   { textAlign: 'right', flexShrink: 0 },
  priceLabel: { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase' },
  price:  { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, lineHeight: 1, color: COLORS.textDark },
  btnDark:    { width: '100%', height: 54, borderRadius: 27, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  btnOutline: { width: '100%', height: 54, borderRadius: 27, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  pending:    { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: COLORS.white, border: 'none', borderBottom: `1px solid ${COLORS.border}`, borderRadius: 0, padding: '14px 0', fontFamily: 'inherit', cursor: 'pointer', color: COLORS.textDark },
  clockTile:  { width: 46, height: 46, borderRadius: 12, background: '#efeadd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700 },
  pendingTitle: { display: 'block', fontWeight: 700, fontSize: 15 },
  pendingSub:   { display: 'block', fontSize: 13, color: COLORS.textMid, marginTop: 1 },
  empty:  { textAlign: 'center', color: COLORS.textMid, fontSize: 14, padding: '40px 16px', lineHeight: 1.5 },
};
